import { z } from "zod";
import type { UIMessage } from "ai";
import type {
  CartAssistantComponent,
  CartAssistantTool,
  LegacyContent,
  LegacyThreadMessage,
} from "./types";

/** Generative UI cards are exposed to the model as tools named `render_<Component>`. */
export const RENDER_TOOL_PREFIX = "render_";

export interface ToolDeclaration {
  name: string;
  description: string;
  inputSchema: Record<string, any>;
}

const EMPTY_OBJECT_SCHEMA = { type: "object", properties: {} };

/** Zod → JSON Schema for the model. Falls back to an open object if a schema can't be represented. */
const toJsonSchema = (schema: z.ZodType<any>): Record<string, any> => {
  try {
    const json = z.toJSONSchema(schema, { target: "draft-7", io: "input", unrepresentable: "any" }) as Record<string, any>;
    delete json.$schema;
    return json.type === "object" ? json : EMPTY_OBJECT_SCHEMA;
  } catch (err) {
    console.warn("[CartAssistant] Could not convert schema to JSON Schema:", err);
    return EMPTY_OBJECT_SCHEMA;
  }
};

export const buildToolDeclarations = (
  tools: CartAssistantTool[],
  components: CartAssistantComponent[],
): ToolDeclaration[] => [
  ...tools.map((t) => ({
    name: t.name,
    description: t.description,
    inputSchema: toJsonSchema(t.inputSchema),
  })),
  ...components.map((c) => ({
    name: `${RENDER_TOOL_PREFIX}${c.name}`,
    description: `Render the ${c.name} card in the chat. ${c.description} Pass the card's props as this tool's input.`,
    inputSchema: toJsonSchema(c.propsSchema),
  })),
];

const toolNameOfPart = (part: any): string | null => {
  if (part.type === "dynamic-tool") return part.toolName;
  if (typeof part.type === "string" && part.type.startsWith("tool-")) return part.type.slice(5);
  return null;
};

const humanizeToolName = (name: string) =>
  name.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/** Converts an AI SDK UIMessage into the message shape the chat UI and cards were built against. */
export const toLegacyMessage = (message: UIMessage, fallbackCreatedAt: number): LegacyThreadMessage => {
  const content: LegacyContent[] = [];
  const reasoning: string[] = [];

  for (const part of message.parts as any[]) {
    if (part.type === "text") {
      content.push({ type: "text", text: part.text });
      continue;
    }
    if (part.type === "reasoning") {
      if (part.text) reasoning.push(part.text);
      continue;
    }
    const toolName = toolNameOfPart(part);
    if (!toolName) continue;

    if (toolName.startsWith(RENDER_TOOL_PREFIX)) {
      content.push({
        type: "component",
        id: part.toolCallId,
        name: toolName.slice(RENDER_TOOL_PREFIX.length),
        props: (part.input as Record<string, any>) || {},
        streamingState: part.state === "input-streaming" ? "streaming" : "done",
      });
    } else {
      content.push({
        type: "tool_use",
        id: part.toolCallId,
        name: toolName,
        input: part.input,
        hasCompleted: part.state === "output-available" || part.state === "output-error",
        statusMessage: humanizeToolName(toolName),
      });
    }
  }

  const createdAt = (message.metadata as any)?.createdAt ?? fallbackCreatedAt;
  return {
    id: message.id,
    role: message.role,
    content,
    createdAt: new Date(createdAt).toISOString(),
    reasoning,
  };
};

/** The tool calls made in the assistant's most recent step (parts after the last step-start). */
export const lastStepToolNames = (message: UIMessage | undefined): string[] => {
  if (!message || message.role !== "assistant") return [];
  const parts = message.parts as any[];
  const lastStepStart = parts.map((p) => p.type).lastIndexOf("step-start");
  return parts
    .slice(lastStepStart + 1)
    .map(toolNameOfPart)
    .filter((n): n is string => !!n);
};

/** Plain text of a message with [img:…] markers removed — used for thread titles. */
export const plainTextOf = (message: UIMessage | undefined): string =>
  ((message?.parts as any[]) || [])
    .filter((p) => p.type === "text")
    .map((p) => p.text as string)
    .join(" ")
    .replace(/\[img:[^\]]+\]/g, "")
    .replace(/\[[A-Z_]+\]:\S+/g, "")
    .trim();
